import * as y from "yup";

export const AdFormSchema = y.object({
  id: y.string().nullable(),
  external_id: y.string().nullable(),
  name: y.string().required("Please provide a name for the adset"),
  campaign_id: y.string().required("Campaign ID is required"),
  campaign_type: y.string().required("Campaign type is required"),
  custom_audience_files: y.array().of(
    y.object({
      id: y.string().nullable(),
      external_id: y.string().nullable(),
      file_key: y.string().required(),
      name: y.string().required(),
      description: y.string().required(),
      schema_fields: y.array().of(y.string().nullable()).nullable()
    })
  ).default([]),
  lifetime_budget: y.number()
    .required("Please set a budget")
    .positive("Budget must be positive")
    .min(1, "Budget must be at least 1"),
  age_min: y.number()
    .required("Please set a minimum age")
    .min(13, "Minimum age must be at least 13"),
  age_max: y.number()
    .required("Please set a maximum age")
    .max(99, "Maximum age must be at most 99"),
  optimization_goal: y.object({
    value: y.string().required("Please select an optimization goal"),
    label: y.string().required()
  }).required("Please select an optimization goal"),
  start_time: y.string()
    .test('is-valid-date', 'Invalid date format', value => !value || !isNaN(Date.parse(value)))
    .test('is-future', 'Start time must be in the future', value => {
      if (!value) return true;
      return new Date(value) > new Date();
    }),
  stop_time: y.string()
    .nullable()
    .when('start_time', {
      is: (startTime: string) => startTime != null,
      then: (schema) => schema
        .test('is-valid-date', 'Invalid date format', value => !value || !isNaN(Date.parse(value)))
        .test('is-after-start', 'Stop time must be after start time', function(value) {
          const startTime = this.parent.start_time;
          if (!value || !startTime) return true;
          return new Date(value) > new Date(startTime);
        })
    }),
  targeting_query: y.string().default(""),
  targeting_type: y.object({
    value: y.string().required("Please select a targeting type"),
    label: y.string().required()
  }).nullable(),
  targeting_subtype: y.object({
    value: y.string(),
    label: y.string()
  }).nullable(),
  audience_ids: y.array().of(
    y.object({
      value: y.string().required(),
      label: y.string().required()
    })
  ).default([]),
  pixel_id: y.object({
    value: y.string().required("Please select a pixel"),
    label: y.string().required()
  }).required("Please select a pixel"),
  geo_markets: y.array().of(
    y.object({
      key: y.string().required(),
      name: y.string().required()
    })
  ).default([]),
  countries: y.array().of(
    y.object({
      id: y.string().required(),
      name: y.string().required()
    })
  ).default(undefined),
  interests: y.array().of(
    y.object({
      id: y.string().required(),
      name: y.string().required()
    })
  ).default([])
}).test('age-range', 'Minimum age must be less than maximum age', function(value) {
  if (value.age_min && value.age_max) {
    return value.age_min <= value.age_max;
  }
  return true;
});

// Type for the form data that matches the schema
export type AdFormData = y.InferType<typeof AdFormSchema>; 